// ============================================
// Firestore Formulary Service
// CRUD for products, bundles, safety rules, and manifest
// ============================================

import {
  collection, doc, getDocs, getDoc, setDoc, deleteDoc,
  writeBatch, query, where, orderBy,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  C_FORMULARY,
  C_SAFETY,
  FORMULARY_DATA_PRODUCTS,
  SC_BUNDLES,
  SC_PRODUCTS,
  SC_RULES,
} from '../../constants/collections';
import type {
  Product, Bundle, SafetyRule,
  FormularyManifest, ManifestProduct, ManifestBundle,
} from '../../types/formulary';

// --- Collection refs ---

const productsCol = () => collection(db, C_FORMULARY, FORMULARY_DATA_PRODUCTS, SC_PRODUCTS);
const bundlesCol = () => collection(db, C_FORMULARY, FORMULARY_DATA_PRODUCTS, SC_BUNDLES);
const safetyCol = () => collection(db, C_SAFETY, FORMULARY_DATA_PRODUCTS, SC_RULES);
const manifestDoc = () => doc(db, C_FORMULARY, 'manifest');

// --- Products ---

export async function getAllProducts(): Promise<Product[]> {
  const snap = await getDocs(query(productsCol(), orderBy('sortOrder')));
  return snap.docs.map((d) => d.data() as Product);
}

export async function getProduct(id: string): Promise<Product | null> {
  const snap = await getDoc(doc(productsCol(), id));
  return snap.exists() ? (snap.data() as Product) : null;
}

export async function saveProduct(product: Product): Promise<void> {
  product.updatedAt = new Date().toISOString();
  await setDoc(doc(productsCol(), product.id), stripUndefined(product));
}

export async function deleteProduct(id: string): Promise<void> {
  await deleteDoc(doc(productsCol(), id));
}

export async function getProductsByTier(tier: number): Promise<Product[]> {
  const snap = await getDocs(query(productsCol(), where('tier', '==', tier), orderBy('sortOrder')));
  return snap.docs.map((d) => d.data() as Product);
}

export async function getProductsByCategory(category: string): Promise<Product[]> {
  const snap = await getDocs(query(productsCol(), where('category', '==', category), orderBy('sortOrder')));
  return snap.docs.map((d) => d.data() as Product);
}

// --- Bundles ---

export async function getAllBundles(): Promise<Bundle[]> {
  const snap = await getDocs(query(bundlesCol(), orderBy('sortOrder')));
  return snap.docs.map((d) => d.data() as Bundle);
}

export async function getBundle(id: string): Promise<Bundle | null> {
  const snap = await getDoc(doc(bundlesCol(), id));
  return snap.exists() ? (snap.data() as Bundle) : null;
}

export async function saveBundle(bundle: Bundle): Promise<void> {
  bundle.updatedAt = new Date().toISOString();
  await setDoc(doc(bundlesCol(), bundle.id), stripUndefined(bundle));
}

export async function deleteBundle(id: string): Promise<void> {
  await deleteDoc(doc(bundlesCol(), id));
}

// --- Safety Rules ---

export async function getAllSafetyRules(): Promise<SafetyRule[]> {
  const snap = await getDocs(safetyCol());
  return snap.docs.map((d) => d.data() as SafetyRule);
}

export async function getActiveSafetyRules(): Promise<SafetyRule[]> {
  const snap = await getDocs(query(safetyCol(), where('isActive', '==', true)));
  return snap.docs.map((d) => d.data() as SafetyRule);
}

export async function saveSafetyRule(rule: SafetyRule): Promise<void> {
  rule.updatedAt = new Date().toISOString();
  await setDoc(doc(safetyCol(), rule.id), stripUndefined(rule));
}

export async function deleteSafetyRule(id: string): Promise<void> {
  await deleteDoc(doc(safetyCol(), id));
}

// --- Manifest ---

export async function getManifest(): Promise<FormularyManifest | null> {
  const snap = await getDoc(manifestDoc());
  return snap.exists() ? (snap.data() as FormularyManifest) : null;
}

export async function regenerateManifest(): Promise<FormularyManifest> {
  const products = await getAllProducts();
  const bundles = await getAllBundles();

  const activeProducts = products.filter((p) => p.status === 'active');
  const activeBundles = bundles.filter((b) => b.status === 'active');

  const manifestProducts: ManifestProduct[] = activeProducts.map((p) => ({
    id: p.id,
    name: p.name,
    category: p.category,
    tier: p.tier,
    dosage: p.dosage,
    timing: p.timing,
    targetBiomarkers: p.targetBiomarkers,
    gender: p.gender,
    contraindications: p.contraindications,
    drugInteractions: p.drugInteractions,
    evidenceLevel: p.evidenceLevel,
    pricing: p.pricing,
    requiresRx: p.requiresRx,
  }));

  const manifestBundles: ManifestBundle[] = activeBundles.map((b) => ({
    id: b.id,
    name: b.name,
    tier: b.tier,
    productIds: b.products.map((bp) => bp.productId),
    productNames: b.products.map((bp) => bp.productName),
    priceRange: b.priceRange,
    target: b.target,
    gender: b.gender,
    requiresProvider: b.requiresProvider,
  }));

  const manifest: FormularyManifest = {
    products: manifestProducts,
    bundles: manifestBundles,
    lastUpdated: new Date().toISOString(),
    productCount: manifestProducts.length,
    bundleCount: manifestBundles.length,
  };

  await setDoc(manifestDoc(), stripUndefined(manifest));
  return manifest;
}

// --- Seed ---

export async function seedFormulary(
  products: Product[],
  bundles: Bundle[],
  rules: SafetyRule[],
  onProgress?: (msg: string) => void,
): Promise<{ products: number; bundles: number; rules: number }> {
  const batchSize = 450; // Firestore max is 500 per batch, leave room
  const allDocs: Array<{ ref: ReturnType<typeof doc>; data: Record<string, unknown> }> = [];

  // Prepare product docs
  for (const p of products) {
    allDocs.push({
      ref: doc(productsCol(), p.id),
      data: stripUndefined(p) as Record<string, unknown>,
    });
  }

  // Prepare bundle docs
  for (const b of bundles) {
    allDocs.push({
      ref: doc(bundlesCol(), b.id),
      data: stripUndefined(b) as Record<string, unknown>,
    });
  }

  // Prepare safety rule docs
  for (const r of rules) {
    allDocs.push({
      ref: doc(safetyCol(), r.id),
      data: stripUndefined(r) as Record<string, unknown>,
    });
  }

  // Write in batches
  for (let i = 0; i < allDocs.length; i += batchSize) {
    const batch = writeBatch(db);
    const chunk = allDocs.slice(i, i + batchSize);
    for (const { ref, data } of chunk) {
      batch.set(ref, data);
    }
    await batch.commit();
    onProgress?.(`Wrote batch ${Math.floor(i / batchSize) + 1} (${chunk.length} docs)`);
  }

  // Generate manifest
  onProgress?.('Regenerating manifest...');
  await regenerateManifest();

  return {
    products: products.length,
    bundles: bundles.length,
    rules: rules.length,
  };
}

// --- Helpers ---

/** Strip undefined values from an object (Firestore rejects undefined) */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = (value !== null && typeof value === 'object') ? stripUndefined(value) : value;
    }
  }
  return cleaned;
}
